/**
 * 团队版算力仪表盘 · 常量与固定映射
 *
 * 集中管理默认参数、时间约束、表格列定义与功能标签样式，
 * 新增功能标签 / 调整列顺序时只需改动此文件。
 */
import dayjs from "dayjs";
import type { ColumnsType } from "ant-design-vue/es/table";
import { ModuleId } from "@xmiles/holopix-types";

/** 快捷时间选项（最近 N 天） */
export const QUICK_DAY_OPTIONS = [
  { label: "最近3天", value: 3 },
  { label: "最近7天", value: 7 },
  { label: "最近30天", value: 30 }
] as const;

/** 默认快捷区间：最近 3 天 */
export const DEFAULT_QUICK_DAY = 3;

/**
 * 后端起始日期：算力流水自 2025-05-24 起开始落库，
 * 早于该日期的区间无数据，统一禁选。
 */
export const BACKEND_START_DATE = dayjs("2025-05-24");

/** 单次查询允许的最大时间跨度（天） */
export const MAX_RANGE_DAYS = 31;

/** 成员表默认分页 */
export const MEMBER_PAGE_SIZE_OPTIONS = ["10", "20", "50"];
export const DEFAULT_MEMBER_PAGE_SIZE = 10;

/** 个人消耗明细默认分页 */
export const DEFAULT_RECORD_PAGE_SIZE = 20;

/**
 * 功能标签样式映射
 *
 * key 为消耗说明的首段功能名（remark.split("/")[0]）。
 * 命中则使用对应主题色，未命中回落到默认 info 蓝。
 * 取值与 PRD 交互原型保持一致（编辑类用紫、生成类用蓝）。
 */
export const FEATURE_TAG_THEME: Record<string, "purple" | "blue" | "green"> = {
  全能编辑: "purple",
  极速编辑: "purple",
  局部细化: "purple",
  涂抹替换: "purple",
  视频生成: "blue",
  一键成稿: "blue",
  丰富细节: "blue",
  相似图裂变: "blue",
  智能扩图: "blue",
  "3D转换": "green",
  自由视角: "green",
  模型定制: "green",
  模型训练: "green"
};

/** 默认标签主题 */
export const DEFAULT_TAG_THEME = "blue" as const;

/**
 * 个人详情 - 消耗明细表列定义
 * （成员用量统计表的列在 MemberTable 内定义，因含排序/插槽与组件强相关）
 */
export const RECORD_COLUMNS: ColumnsType = [
  { title: "消耗时间", dataIndex: "ctime", key: "ctime", ellipsis: true },
  { title: "消耗说明", dataIndex: "remark", key: "remark", ellipsis: true },
  { title: "功能", dataIndex: "feature", key: "feature", ellipsis: true },
  { title: "算力值", dataIndex: "points", key: "points", align: "right", ellipsis: true }
];

/** 成员额度预警阈值（达到即提醒联系管理员调拨） */
export const QUOTA_WARN_PCT = 95;

/** 成员额度拦截阈值（达到即暂停新的付费生成） */
export const QUOTA_BLOCK_PCT = 100;

/** 成员角色值（自视角固定为成员；与 @/api/group 的 UserRole.Member 对齐，避免在纯函数中引入 IO 依赖） */
export const SELF_VIEW_ROLE = 2;

/** 额度输入步进（按 100 取整） */
export const QUOTA_STEP = 100;

/**
 * 团队额度健康度分级阈值（已用百分比）
 * 70 关注 / 85 预警 / 95 紧急，低于 70 为正常。
 */
export const HEALTH_STAGES = [
  { level: "watch", pct: 70, name: "70% 关注" },
  { level: "warn", pct: 85, name: "85% 预警" },
  { level: "urgent", pct: 95, name: "95% 紧急" }
] as const;

/** 可限制的模型能力配置（一期 MVP 仅 3 个） */
export interface ModelCapConfig {
  /** 唯一标识 */
  key: string;
  /** 分类（图片编辑 / 视频生成） */
  kind: string;
  /** 模型名称 */
  name: string;
  /** 关联的 moduleTypeId 列表（为后续 90712 联动预留） */
  moduleTypeIds: number[];
}

export const MODEL_CAPS: ModelCapConfig[] = [
  {
    key: "imageEditEnterprise",
    kind: "图片编辑",
    name: "全能编辑 V3 企业专线",
    moduleTypeIds: [ModuleId.GptImage2Enterprise]  // 46
  },
  {
    key: "videoSeedance2",
    kind: "视频生成",
    name: "Seedance 2.0",
    moduleTypeIds: [
      ModuleId.SeedanceStartEndVideo,   // 55
      ModuleId.SeedanceReferenceVideo,  // 56
      ModuleId.SeedanceImage2Video,     // 57
      ModuleId.SeedanceText2Video,      // 64
    ]
  },
  {
    key: "videoVidu2Pro",
    kind: "视频生成",
    name: "Vidu 2.0 Pro",
    moduleTypeIds: [
      ModuleId.CanvasStartEndVideo,   // 36
      ModuleId.CanvasReferenceVideo,  // 37
      ModuleId.CanvasImage2Video,     // 43
    ]
  }
];
